import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToUserCollection, addUserDoc, updateUserDoc, deleteUserDoc } from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/inventory-pages.css';

const emptyForm = {
  name: '',
  contact: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  category: '',
  notes: '',
};

const CATEGORIES = [
  'Oils & Butters',
  'Lye & Chemicals',
  'Fragrances & Essential Oils',
  'Colours & Micas',
  'Additives',
  'Packaging',
  'General',
];

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeToUserCollection('suppliers', setSuppliers);
    return () => unsub();
  }, []);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (supplier) => {
    setForm({
      name: supplier.name || '',
      contact: supplier.contact || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      website: supplier.website || '',
      address: supplier.address || '',
      category: supplier.category || '',
      notes: supplier.notes || '',
    });
    setEditId(supplier.id);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setSaving(true);
    try {
      if (editId) {
        await updateUserDoc('suppliers', editId, form);
      } else {
        await addUserDoc('suppliers', form);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditId(null);
    } catch (err) {
      console.error('Error saving supplier:', err);
      alert('Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await deleteUserDoc('suppliers', id);
    } catch (err) {
      console.error('Error deleting supplier:', err);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return suppliers;
    const q = search.toLowerCase();
    return suppliers.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q) ||
      (s.contact || '').toLowerCase().includes(q)
    );
  }, [suppliers, search]);

  return (
    <>
      <div className="sl-page inventory-subpage">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Inventory', to: '/inventory' },
            { label: 'Suppliers' },
          ]} />
          <div className="pageheader-row">
            <div className="pageheading">Suppliers</div>
            <div className="pagedescription">Manage your ingredient and packaging suppliers.</div>
          </div>
        </div>

        <div className="inventory-subpage-content borderleft">
          {/* Stats */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-stats-row">
              <div className="inv-stat-item">
                <span className="inv-stat-label">Total Suppliers</span>
                <span className="inv-stat-value">{suppliers.length}</span>
              </div>
              <div className="inv-stat-item">
                <span className="inv-stat-label">Categories</span>
                <span className="inv-stat-value">{new Set(suppliers.map(s => s.category).filter(Boolean)).size}</span>
              </div>
            </div>
          </section>

          {/* Toolbar */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-search">
              <input
                type="text"
                placeholder="Search suppliers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button className="inv-btn inv-btn-primary" onClick={openCreate}>
                + Add Supplier
              </button>
            </div>
          </section>

          {/* Supplier cards */}
          <section className="inv-card mypaddingmoretop">
            {filtered.length === 0 ? (
              <div className="inv-empty">
                <p>{suppliers.length === 0 ? 'No suppliers added yet.' : 'No suppliers match your search.'}</p>
                {suppliers.length === 0 && (
                  <button className="inv-btn inv-btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>
                    Add First Supplier
                  </button>
                )}
              </div>
            ) : (
              <div className="inv-card-grid">
                {filtered.map(s => (
                  <div key={s.id} className="inv-card-item">
                    <div className="inv-card-item-header">
                      <div className="inv-card-item-title">{s.name}</div>
                      {s.category && <span className="inv-badge inv-badge-blue">{s.category}</span>}
                    </div>
                    <div className="inv-card-item-meta">
                      {s.contact && <span>{s.contact}</span>}
                      {s.phone && <span>{s.phone}</span>}
                      {s.email && <span>{s.email}</span>}
                      {s.website && (
                        <span>🔗 <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{s.website}</a></span>
                      )}
                      {s.address && <span>{s.address}</span>}
                      {s.notes && <span style={{ color: 'var(--sl-gray-400)', fontStyle: 'italic' }}>{s.notes}</span>}
                    </div>
                    <div className="inv-card-item-actions">
                      <button className="inv-btn inv-btn-secondary inv-btn-sm" onClick={() => openEdit(s)}>Edit</button>
                      <button className="inv-btn inv-btn-danger inv-btn-sm" onClick={() => handleDelete(s.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
     
      {/* Modal */}
      {showModal && (
        <div className="inv-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <h2>{editId ? 'Edit Supplier' : 'Add Supplier'}</h2>
            <form onSubmit={handleSave}>
              <div className="inv-form-grid">
                <div className="inv-field">
                  <label>Supplier Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. AromaZone India" required />
                </div>
                <div className="inv-field">
                  <label>Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="inv-field">
                  <label>Contact Person</label>
                  <input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Name" />
                </div>
                <div className="inv-field">
                  <label>Phone</label>
                  <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 ..." />
                </div>
                <div className="inv-field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
                </div>
                <div className="inv-field">
                  <label>Website</label>
                  <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />
                </div>
                <div className="inv-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Address</label>
                  <textarea rows={2} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full address" />
                </div>
                <div className="inv-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes..." />
                </div>
              </div>
              <div className="inv-modal-actions">
                <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn inv-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Update' : 'Add Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
       </div>

    </>
  );
}
